import React from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';

import {
  CmBox,
  CmErrorText,
  CmFlex,
  CmFormControl,
  CmHeading,
  CmIconButton,
  CmScrollBox,
  CmStack,
} from '@/components/uiParts';
import {
  CloseIcon,
  DragHandleIcon,
  EditIcon,
  RepeatClockIcon,
} from '@/components/uiParts';
import {
  CustomGroup,
  DIVIDED_CONTENT_WIDTH,
} from '@/pages/link-items/LinkItemsPage';

import {
  AccordionLinkedItemsArea,
  ExpectedDeleteMask,
  GroupNameErrorMessage,
  GroupNameInput,
  RadioCard,
} from './LinkedItemCard';

interface Props {
  minusHeight: number;
  linkedGroups: CustomGroup[];
  targetGroupIndex: number;
  handleChangeTargetGroupIndex: (value: number) => void;
  handleInputGroupName: (groupName: string, index: number) => void;
  handleDeleteGroupItem: (groupIndex: number, itemIndex: number) => void;
  handleDeleteGroup: (
    targetEvent: 'maskGroup' | 'deleteGroup',
    groupIndex: number
  ) => void;
  handleRemoveMask: (groupIndex: number) => void;
  handleAddEditGroupNameInput: (
    editGroupName: string,
    groupIndex: number
  ) => void;
  handleEditGroupName: (groupName: string, index: number) => void;
  handleReturnEditGroupNameInput: (groupIndex: number) => void;
  handleOnDragStart: () => void;
  handleToggleGroupAccordion: (groupIndex: number) => void;
  handleOnDragEnd: (result: DropResult) => void;
}

export const AddedLinkitemArea: React.FC<Props> = ({
  minusHeight,
  linkedGroups,
  targetGroupIndex,
  handleChangeTargetGroupIndex,
  handleInputGroupName,
  handleDeleteGroupItem,
  handleDeleteGroup,
  handleRemoveMask,
  handleAddEditGroupNameInput,
  handleEditGroupName,
  handleReturnEditGroupNameInput,
  handleOnDragStart,
  handleToggleGroupAccordion,
  handleOnDragEnd,
}) => {
  return (
    <CmScrollBox
      width={DIVIDED_CONTENT_WIDTH}
      height={`calc(100vh - ${minusHeight}px)`}
    >
      <CmStack>
        <DragDropContext
          onDragStart={handleOnDragStart}
          onDragEnd={handleOnDragEnd}
        >
          <Droppable droppableId="LinkedGroups">
            {(provided) => (
              <ul {...provided.droppableProps} ref={provided.innerRef}>
                {linkedGroups.map((group, groupIndex) => {
                  const uniqueGroupId = `group-${groupIndex}`;
                  return (
                    <Draggable
                      key={groupIndex}
                      draggableId={uniqueGroupId}
                      index={groupIndex}
                    >
                      {(provided) => (
                        <li
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <CmBox pb={2}>
                            <RadioCard
                              key={groupIndex}
                              w="100%"
                              isChecked={targetGroupIndex === groupIndex}
                              onClick={() =>
                                handleChangeTargetGroupIndex(groupIndex)
                              }
                              {...{ uniqueGroupId }}
                            >
                              {group.isTentativeDelete && (
                                <ExpectedDeleteMask
                                  {...{ groupIndex, handleRemoveMask }}
                                />
                              )}
                              <CmFlex
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <DragHandleIcon aria-label="ドラッグ" />
                                <CmBox
                                  w="calc(100% - 25px)"
                                  p="2"
                                  position="relative"
                                >
                                  {/*
                                   // @ts-ignore TS2590 */}
                                  <CmIconButton
                                    aria-label="Checked Notification"
                                    position="absolute"
                                    top="-5px"
                                    right="-5px"
                                    cursor="pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteGroup(
                                        group.group_name === undefined
                                          ? 'deleteGroup'
                                          : 'maskGroup',
                                        groupIndex
                                      );
                                    }}
                                    icon={<CloseIcon />}
                                  />
                                  <CmStack spacing="2">
                                    {group.group_name ? (
                                      <>
                                        {group.editGroupName !== undefined ? (
                                          <CmFormControl width="85%">
                                            <CmFlex>
                                              <GroupNameInput
                                                value={group.editGroupName}
                                                handleGroupName={
                                                  handleEditGroupName
                                                }
                                                {...{ groupIndex }}
                                              />
                                              <CmIconButton
                                                ml="2"
                                                icon={<RepeatClockIcon />}
                                                aria-label="cancel"
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleReturnEditGroupNameInput(
                                                    groupIndex
                                                  );
                                                }}
                                              />
                                            </CmFlex>
                                            <GroupNameErrorMessage
                                              validate={group.validate}
                                            />
                                          </CmFormControl>
                                        ) : (
                                          group.group_name !== undefined && (
                                            <CmFlex
                                              justifyContent="space-between"
                                              alignItems="center"
                                              width="85%"
                                            >
                                              <CmHeading
                                                mb="1"
                                                pb="1"
                                                borderBottom="1px solid #000"
                                                width="full"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleAddEditGroupNameInput(
                                                    // NOTE group.group_name !== undefined で確認をしているが、TSの型ガードがうまく伝わっていないため ! で対応をする。
                                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                                    group.group_name!,
                                                    groupIndex
                                                  );
                                                }}
                                              >
                                                {group.group_name}
                                              </CmHeading>
                                              <CmIconButton
                                                ml="2"
                                                mt="-3px"
                                                icon={<EditIcon />}
                                                aria-label="cancel"
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleAddEditGroupNameInput(
                                                    // NOTE group.group_name !== undefined で確認をしているが、TSの型ガードがうまく伝わっていないため ! で対応をする。
                                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                                    group.group_name!,
                                                    groupIndex
                                                  );
                                                }}
                                              />
                                            </CmFlex>
                                          )
                                        )}
                                      </>
                                    ) : (
                                      // NOTE 追加カード以外はinputGroupNameがundefinedのため
                                      group.inputGroupName !== undefined &&
                                      group.validate !== undefined && (
                                        <CmFormControl width="85%">
                                          <GroupNameInput
                                            value={group.inputGroupName}
                                            handleGroupName={
                                              handleInputGroupName
                                            }
                                            {...{ groupIndex }}
                                          />
                                          <GroupNameErrorMessage
                                            validate={group.validate}
                                          />
                                        </CmFormControl>
                                      )
                                    )}
                                    {group.linked_store_items.length > 0 ? (
                                      <AccordionLinkedItemsArea
                                        {...{
                                          group,
                                          handleDeleteGroupItem,
                                          handleToggleGroupAccordion,
                                          groupIndex,
                                        }}
                                      />
                                    ) : (
                                      <CmErrorText>
                                        ※紐づけるアイテムを追加してください。
                                      </CmErrorText>
                                    )}
                                  </CmStack>
                                </CmBox>
                              </CmFlex>
                            </RadioCard>
                          </CmBox>
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </CmStack>
    </CmScrollBox>
  );
};
